import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCommentsGateway } from './CommentsGatewayProvider';
import { toggleCommentLike } from '../api/commentCache';
import { getCommentsV2QueryOptions } from '../api/queryOptions';
import type { Comment } from '../model/types';

import { useUserScopedKey } from '@/hooks/useUserScopedQuery';
import { trackCommentLikeFailed, trackCommentLikeToggled } from '@/util/analyticsEvents';

/**
 * Likes or unlikes a comment on a post's thread: flips it at once, rolls back
 * if the request fails, and refreshes the thread once it settles. `liked` in
 * the variables is the state before the press. Telling the reader about a
 * failure is the caller's, through `onFailed`, since a hook draws nothing.
 */
export function useToggleCommentLike({
  postId,
  onFailed,
}: {
  postId: number;
  onFailed?: (variables: { commentId: number; liked: boolean }) => void;
}) {
  const queryClient = useQueryClient();

  const commentsKey = useUserScopedKey(getCommentsV2QueryOptions);

  const gateway = useCommentsGateway();

  return useMutation<
    void,
    Error,
    { commentId: number; liked: boolean },
    { previousComments: Comment[]; postId: number }
  >({
    mutationFn: ({ commentId, liked }) => gateway.setLiked(commentId, !liked),
    onMutate: async ({ commentId }) => {
      const queryKey = commentsKey(postId);

      await queryClient.cancelQueries({ queryKey });

      const previousComments = queryClient.getQueryData(queryKey) ?? [];

      queryClient.setQueryData(queryKey, toggleCommentLike(previousComments, commentId));

      return { previousComments, postId };
    },
    onSuccess: (_data, variables) => {
      trackCommentLikeToggled({ commentId: variables.commentId, liked: !variables.liked });
    },
    onError: (error, variables, context) => {
      trackCommentLikeFailed({
        commentId: variables.commentId,
        liked: !variables.liked,
        error: error instanceof Error ? error.message : JSON.stringify(error),
      });

      if (!context) {
        return;
      }

      queryClient.setQueryData(commentsKey(context.postId), context.previousComments);

      onFailed?.(variables);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: commentsKey(postId),
      });
    },
  });
}
